// src/lib/reports.ts — Reports applet types, formatters, and API client.
//
// The snapshot type mirrors what the reports.snapshot Scheduler job
// (api/src/reports-snapshot.ts) writes into hub_reports_snapshot, which in turn
// mirrors reports.nymr.co.uk. All money is whole GBP pounds.

import { apiFetch } from "../api";

export interface RevenueMonth {
  m: string;
  ty: number;
  ly: number;
  partial?: boolean;
}

export interface ReportsSnapshot {
  refreshedAt: string;
  generatedBy: string;
  fyLabel: string;
  fyPrevLabel: string;
  asOf: string;
  netRevenue: { fyToDate: number; calToDate: number; fyPrevToDate: number; inclForward: number };
  giftAid: { fyToDate: number; fyPrevToDate: number; inclForward: number };
  footfall: { fyToDate: number; fyPrevToDate: number };
  revenueByMonth: RevenueMonth[];
  pullman: {
    netFyToDate: number;
    netThisMonth: number;
    monthLabel: string;
    capacityNext30: number;
    seatsBooked: number;
    seatsAvailable: number;
    servicesNext30: number;
  };
  events: {
    last30Net: number;
    last30Count: number;
    last30Items: { name: string; net: number }[];
    next30Net: number;
    next30Count: number;
    next30Items: { name: string; net: number }[];
  };
}

// GET /api/reports/snapshot. payload is null before the first job run (the UI
// shows a graceful "first refresh pending" state). canExec drives the Executive
// tab (single source of truth, same as /api/me).
export interface SnapshotResponse {
  refreshedAt: string | null;
  snapshotDate: string | null;
  payload: ReportsSnapshot | null;
  canExec: boolean;
}

// The pinnable measures, in display order. id is the home-pin slug; it must
// match PIN_SLUGS in api/src/resources/reports.ts.
export type MeasureId =
  | "net-revenue"
  | "gift-aid"
  | "footfall"
  | "revenue-yoy"
  | "pullman-net"
  | "pullman-capacity"
  | "events-net";

export interface Measure {
  id: MeasureId;
  label: string;
}

export const MEASURES: Measure[] = [
  { id: "net-revenue", label: "Net revenue to date" },
  { id: "gift-aid", label: "Gift Aid" },
  { id: "footfall", label: "Footfall" },
  { id: "revenue-yoy", label: "Revenue — this year vs last" },
  { id: "pullman-net", label: "Pullman net" },
  { id: "pullman-capacity", label: "Pullman capacity — next 30 days" },
  { id: "events-net", label: "Events net" },
];

// ── formatters (ported verbatim from the prototype reports-data.jsx) ──

export function fmtGBP(n: number, opts: { compact?: boolean; pence?: boolean } = {}): string {
  const { compact = false, pence = false } = opts;
  if (compact) {
    if (Math.abs(n) >= 1e6) return "£" + (n / 1e6).toFixed(2).replace(/\.?0+$/, "") + "M";
    if (Math.abs(n) >= 1e3) return "£" + Math.round(n / 1e3) + "k";
  }
  return "£" + n.toLocaleString("en-GB", { minimumFractionDigits: pence ? 2 : 0, maximumFractionDigits: pence ? 2 : 0 });
}

export function fmtNum(n: number): string {
  return n.toLocaleString("en-GB");
}

export function pct(n: number): string {
  return (n >= 0 ? "+" : "") + (n * 100).toFixed(1) + "%";
}

/** Safe YoY delta (fraction) from a current + prior value; 0 when prior is 0. */
export function yoy(now: number, prev: number): number {
  return prev ? (now - prev) / prev : 0;
}

// ── API client ──

export function getReportsSnapshot(): Promise<SnapshotResponse> {
  return apiFetch<SnapshotResponse>("/api/reports/snapshot");
}

export function getReportsPin(): Promise<{ pin: MeasureId | null }> {
  return apiFetch<{ pin: MeasureId | null }>("/api/reports/pin");
}

export function putReportsPin(pin: MeasureId | null): Promise<{ pin: MeasureId | null }> {
  return apiFetch<{ pin: MeasureId | null }>("/api/reports/pin", {
    method: "PUT",
    body: JSON.stringify({ pin }),
  });
}
